import * as THREE from 'three';

/**
 * Wrist HUD.
 *
 * The flat game's HUD is DOM (`src/ui`), layered over the canvas by the
 * browser's compositor. A WebXR session does not composite the DOM into the
 * headset at all, so in VR that HUD is simply not there — none of it can be
 * reused, and the usual fix (head-locked panels) is the worst option available:
 * anything welded to the eye cannot be looked away from, sits at a fixed
 * vergence the eye keeps trying to focus past, and occludes the world the
 * player is trying to fight in.
 *
 * So the readouts go on the left wrist, which is the convention for good
 * reasons: it is glanceable on purpose rather than always-on, it inherits real
 * depth from the arm's actual distance, and checking your ammo becomes the same
 * physical gesture it is with a real weapon.
 *
 * One 256x256 canvas, redrawn only when a displayed value actually changes.
 */

const W = 256;
const H = 256;

export class WristHud {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = W;
    this.canvas.height = H;
    this.g = this.canvas.getContext('2d');

    this.texture = new THREE.CanvasTexture(this.canvas);
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.generateMipmaps = false;

    const material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: false,
      toneMapped: false, // an emissive panel, not a surface the exposure meters
      side: THREE.DoubleSide,
    });
    material.userData.owNoPatch = true;

    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.11, 0.11), material);
    this.mesh.name = 'xr-wrist-hud';
    // Sat on the back of the left wrist, tilted up toward the face the way a
    // watch is: readable with a glance, edge-on and out of the way otherwise.
    this.mesh.position.set(0, 0.035, 0.055);
    this.mesh.rotation.set(-Math.PI * 0.5 + 0.55, 0, 0);
    this.mesh.renderOrder = 5000;
    this.mesh.userData.owNoPrepass = true;
    this.mesh.userData.owNoShadow = true;

    this._prev = { hp: -1, ammo: -1, reserve: -1, mag: -1, name: '', dead: false };
    this.draw({ hp: 100, maxHp: 100, ammo: 30, reserve: 120, magSize: 30, name: '', dead: false });
  }

  /**
   * @param {{hp:number,maxHp:number,ammo:number,reserve:number,magSize:number,
   *          name:string,dead:boolean}} s
   */
  sync(s) {
    const p = this._prev;
    if (
      s.hp === p.hp && s.ammo === p.ammo && s.reserve === p.reserve &&
      s.magSize === p.mag && s.name === p.name && s.dead === p.dead
    ) return;
    p.hp = s.hp; p.ammo = s.ammo; p.reserve = s.reserve;
    p.mag = s.magSize; p.name = s.name; p.dead = s.dead;
    this.draw(s);
  }

  draw(s) {
    const g = this.g;
    g.clearRect(0, 0, W, H);

    // Panel
    g.fillStyle = 'rgba(8,11,13,0.86)';
    roundRect(g, 6, 6, W - 12, H - 12, 18);
    g.fill();
    g.strokeStyle = 'rgba(150,180,190,0.30)';
    g.lineWidth = 2;
    g.stroke();

    const hpFrac = Math.max(0, Math.min(1, s.maxHp > 0 ? s.hp / s.maxHp : 0));
    const critical = hpFrac <= 0.34;

    // ---- health ----------------------------------------------------------
    g.font = '600 17px ui-monospace, "SF Mono", Menlo, monospace';
    g.fillStyle = 'rgba(170,190,200,0.72)';
    g.fillText('HEALTH', 26, 44);

    g.fillStyle = 'rgba(255,255,255,0.10)';
    roundRect(g, 26, 54, W - 52, 16, 8);
    g.fill();
    g.fillStyle = s.dead ? '#6a1b1b' : critical ? '#e0463c' : '#cfe6dd';
    const barW = Math.max(2, (W - 52) * hpFrac);
    roundRect(g, 26, 54, barW, 16, 8);
    g.fill();

    g.font = '700 30px ui-monospace, "SF Mono", Menlo, monospace';
    g.fillStyle = s.dead ? '#e0463c' : '#e8f2ee';
    g.fillText(s.dead ? 'DOWN' : String(Math.max(0, Math.round(s.hp))), 26, 104);

    // ---- ammo ------------------------------------------------------------
    g.font = '600 17px ui-monospace, "SF Mono", Menlo, monospace';
    g.fillStyle = 'rgba(170,190,200,0.72)';
    g.fillText('AMMO', 26, 146);

    const empty = s.ammo <= 0;
    g.font = '700 46px ui-monospace, "SF Mono", Menlo, monospace';
    g.fillStyle = empty ? '#e0463c' : '#e8f2ee';
    const magText = String(s.ammo);
    g.fillText(magText, 26, 194);
    const magW = g.measureText(magText).width;

    g.font = '600 24px ui-monospace, "SF Mono", Menlo, monospace';
    g.fillStyle = 'rgba(170,190,200,0.62)';
    g.fillText(`/ ${s.reserve}`, 26 + magW + 12, 194);

    if (s.name) {
      g.font = '600 14px ui-monospace, "SF Mono", Menlo, monospace';
      g.fillStyle = 'rgba(150,175,185,0.55)';
      g.fillText(String(s.name).toUpperCase(), 26, 224);
    }

    this.texture.needsUpdate = true;
  }

  dispose() {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
    this.texture.dispose();
    this.mesh.parent?.remove(this.mesh);
  }
}

function roundRect(g, x, y, w, h, r) {
  const rr = Math.min(r, w * 0.5, h * 0.5);
  g.beginPath();
  g.moveTo(x + rr, y);
  g.arcTo(x + w, y, x + w, y + h, rr);
  g.arcTo(x + w, y + h, x, y + h, rr);
  g.arcTo(x, y + h, x, y, rr);
  g.arcTo(x, y, x + w, y, rr);
  g.closePath();
}
